import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import browseFilterIcon from '../../../assets/images/browse-filter-icon.svg';
import {
  EXPERIENCE_LEVEL_KEYS,
  REQUIRED_EDUCATION_KEYS,
  SKILLS_KEYS,
  WORK_PLACE_TYPE_KEYS,
} from '../../../constants/commonConstants.js';
import { DEFAULT_PAGE } from '../../../constants/paginationDefaults.js';
import { useJobFilterContext } from '../../../contexts/JobFilterProvider';
import { selectFilterDropdownValues, selectSkills } from '../../../selectors/filterSelectors';
import ButtonWithIcon from '../../shared/ButtonWithIcon/ButtonWithIcon';
import CustomMultiSelect from '../../shared/CustomMultiSelect/CustomMultiSelect';

import styles from './BrowseFiltersOption.module.scss';

export const BrowseFiltersOption = ({ setfilters, isDropdownVisible, setIsDropdownVisible }) => {
  const { t } = useTranslation('common');
  const { jobFilterValues, updateSearchParams, updateJobFilterValues, getTotalSelectedCount } = useJobFilterContext();
  const { workType, experienceLevel, requiredEducation, skills } = jobFilterValues;
  const skillOptions = useSelector(selectSkills);
  const getOptionIds = (options) => options.map((option) => option.value);

  const getWorkPlaceTypeLabel = (name) =>
    WORK_PLACE_TYPE_KEYS[name] ? t(`workPlaceType.${WORK_PLACE_TYPE_KEYS[name]}`) : name;

  const getExperienceLevelLabel = (name) =>
    EXPERIENCE_LEVEL_KEYS[name] ? t(`experienceLevel.${EXPERIENCE_LEVEL_KEYS[name]}`) : name;

  const getRequiredEducationLabel = (name) =>
    REQUIRED_EDUCATION_KEYS[name] ? t(`requiredEducation.${REQUIRED_EDUCATION_KEYS[name]}`) : name;

  const getSkillLabel = (name) => (SKILLS_KEYS[name] ? t(`skills.${SKILLS_KEYS[name]}`) : name);

  const isWorkPlaceType = (types) => types === 'work_place_types' || types === 'work_place_type';

  const getOptionLabel = (option, types) => {
    if (isWorkPlaceType(types)) return getWorkPlaceTypeLabel(option.name);
    if (types === 'experience_level') return getExperienceLevelLabel(option.name);
    if (types === 'required_education') return getRequiredEducationLabel(option.name);
    if (types === 'skills') return getSkillLabel(option.name);
    return option.name;
  };

  const selectOptions = (options, types = null) =>
    options.map((option) => ({
      label: getOptionLabel(option, types),
      value: isWorkPlaceType(types) ? option.name : option.id,
    }));

  const {
    workTypes: workTypeOptions,
    experienceLevels: experienceLevelOptions,
    requiredEducation: educationOptions,
  } = useSelector(selectFilterDropdownValues);

  const selectTypeValues = (ids, typeOption, optionType = null) =>
    selectOptions(typeOption, optionType)?.filter((option) => ids.includes(option.value));

  const handleOnChange = (type, option) => {
    updateJobFilterValues(type, getOptionIds(option));
    updateSearchParams('id', '');
    setfilters((prevFilters) => ({
      ...prevFilters,
      filters: {
        ...prevFilters.filters,
        work_place_types: type === 'workType' ? getOptionIds(option) : workType,
        experience: type === 'experienceLevel' ? getOptionIds(option) : experienceLevel,
        education: type === 'requiredEducation' ? getOptionIds(option) : requiredEducation,
        skills: type === 'skills' ? getOptionIds(option) : skills,
        page: DEFAULT_PAGE,
      },
    }));
  };

  const totalSelectedCount = getTotalSelectedCount();

  const getContainerClass = () => {
    if (isDropdownVisible) return styles.filterButton;
    if (totalSelectedCount > 0) return styles.filterButtonActive;
    return null;
  };

  return (
    <div className={styles.filterContainer}>
      <ButtonWithIcon
        img={browseFilterIcon}
        text={
          totalSelectedCount > 0 ? (
            <span className={styles.filterText}>
              {t('browse:filters.title')} <span className={styles.countText}>{totalSelectedCount}</span>
            </span>
          ) : (
            t('browse:filters.title')
          )
        }
        imgClassName="btn-icon-36"
        textClassName={styles.btnText}
        variant="tertiary-36"
        containerClass={getContainerClass()}
        onClick={() => setIsDropdownVisible(true)}
      />

      <span className={styles.verticalLine} />
      <div className={styles.filtersValueContianer}>
        <CustomMultiSelect
          options={selectOptions(workTypeOptions, 'work_place_types')}
          value={selectTypeValues(workType, workTypeOptions, 'work_place_type')}
          onChange={(selectedOptions) => handleOnChange('workType', selectedOptions)}
          placeholder={t('placeholders.workPlaceType')}
          countValue={workType.length || null}
          isSearchable={false}
          isfilterByButton
        />
        <CustomMultiSelect
          options={selectOptions(experienceLevelOptions, 'experience_level')}
          value={selectTypeValues(experienceLevel, experienceLevelOptions, 'experience_level')}
          onChange={(selectedOptions) => handleOnChange('experienceLevel', selectedOptions)}
          placeholder={t('placeholders.experienceLevel')}
          countValue={experienceLevel.length || null}
          isSearchable={false}
          isfilterByButton
        />
        <CustomMultiSelect
          options={selectOptions(educationOptions, 'required_education')}
          value={selectTypeValues(requiredEducation, educationOptions, 'required_education')}
          onChange={(selectedOptions) => handleOnChange('requiredEducation', selectedOptions)}
          placeholder={t('placeholders.requiredEducation')}
          countValue={requiredEducation.length || null}
          isSearchable={false}
          isfilterByButton
        />
        <CustomMultiSelect
          options={selectOptions(skillOptions, 'skills')}
          value={selectTypeValues(skills, skillOptions, 'skills')}
          onChange={(selectedOptions) => handleOnChange('skills', selectedOptions)}
          placeholder={t('placeholders.skills')}
          countValue={skills.length || null}
          isSearchable={false}
          isfilterByButton
        />
      </div>
    </div>
  );
};
